/**
 * TCB 公共能力：初始化 app + callFunction 封装 + Storage 凭证直传（add-auth-login）。
 *
 * 设计要点：
 * - 所有 TCB 调用复用同一个 app 单例，避免重复 init 触发 CloudBase 警告
 * - 鉴权：callFunction 自动从本地会话注入 `data.token`（不再注入 tenantId）
 * - 传输层：spike-5 真 TCB 结论——SDK 无会话时 `callFunction` 平台层直接返回
 *   `MISSING_CREDENTIALS`（公开函数也无法到达），必须先匿名登录建立会话；
 *   匿名会话仅是传输通道，业务安全由云函数自有中间件链经 `data.token` 保证
 * - callFunction 自动 unwrap ResultEnvelope<T>，失败抛 Error(message + code)
 * - UNAUTHENTICATED → 清登录态跳 /login（带回跳）；FORBIDDEN → 统一提示（留在当前页）
 * - Storage：上传 = 云函数签发临时凭证 → 客户端直传 COS（无 TCB 会话依赖）；
 *   下载 = 云函数签发临时 URL；删除 = 业务云函数服务端执行（客户端不再直接删）
 */
import type { ResultEnvelope } from '@cms/shared'
import { resolveFunctionDomain } from '@cms/shared/function-routing'
import type cloudbaseSdk from '@cloudbase/js-sdk'
import { ElMessage } from 'element-plus'
import { getSessionToken, clearSession, hasSession } from './auth-session'
import { clearPlatformToken, getPlatformToken } from './platform-session'
import { getPageSessionScope } from './session-scope'
import { configureFrontendLogTransport, createFrontendLogger } from './logger'

type CloudBaseSdk = typeof cloudbaseSdk
type TcbApp = ReturnType<CloudBaseSdk['init']>
let _appPromise: Promise<TcbApp> | null = null
let _authReady: Promise<void> | null = null
const PUBLIC_WEB_FUNCTIONS = new Set([
  'auth-login',
  'auth-web-password-login',
  'auth-web-password-select',
  'wx-web-login',
  'ping',
  'log-report',
])
const retiringEmployeeTokens = new Set<string>()
const employeeRequests = new Map<string, Set<Promise<unknown>>>()
const LOGOUT_REQUEST_WAIT_MS = 5_000

/** 员工会话开始退出后禁止新业务请求；后来登录的不同 token 不受影响。 */
export function isEmployeeSessionRetiring(token: string | null): boolean {
  return token !== null && retiringEmployeeTokens.has(token)
}

/** 先冻结员工会话，再等待已发送请求；最多五秒后仍允许服务端撤销会话。 */
export async function drainEmployeeRequestsForLogout(token: string | null): Promise<void> {
  if (!token) return
  retiringEmployeeTokens.add(token)
  // 冻结发生在快照之前；尚在 SDK 初始化/认证阶段的请求会在发送前取消。
  const requests = [...(employeeRequests.get(token) ?? [])]
  if (requests.length === 0) return
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    // SDK 的 timeout 不是硬 HTTP 截止时间；超预算请求不重放，原错误仍被记录。
    await Promise.race([
      Promise.allSettled(requests),
      new Promise<void>((resolve) => { timer = setTimeout(resolve, LOGOUT_REQUEST_WAIT_MS) }),
    ])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

function cancelledSessionRequest(): Error {
  const error = new Error('请求已取消：员工会话正在退出或已经切换')
  error.name = 'AbortError'
  return error
}

async function getApp(): Promise<TcbApp> {
  if (_appPromise) return _appPromise
  const envId = import.meta.env.VITE_TCB_ENV_ID
  if (!envId) {
    throw new Error(
      '[tcb] VITE_TCB_ENV_ID 未配置。请在 .env 中设置腾讯云开发环境 ID。',
    )
  }
  _appPromise = import('@cloudbase/js-sdk')
    .then((module) => {
      const cloudbase = (module as unknown as { default: CloudBaseSdk }).default
      // 覆盖云函数 45 秒冷态连接及业务处理预算，并为传输开销留出时间。
      return cloudbase.init({ env: envId, timeout: 60_000 })
    })
    .catch((error: unknown) => {
      _appPromise = null
      throw error
    })
  return _appPromise
}

/**
 * 传输层匿名会话（spike-5 结论）：
 * SDK 无会话时 `callFunction` 平台层返回 `MISSING_CREDENTIALS`（公开函数也无法到达），
 * 必须先 `signInAnonymously()` 建立会话。该会话不携带任何业务身份——
 * 业务安全完全由云函数中间件链经 `data.token` 保证。
 * 幂等：已有登录态则跳过。
 */
export async function ensureTransportAuth(): Promise<void> {
  if (_authReady) return _authReady
  const app = await getApp()
  _authReady = (async () => {
    const auth = app.auth()
    const loginState = await auth.getLoginState()
    if (!loginState) {
      await auth.signInAnonymously()
    }
  })().catch((err) => {
    _authReady = null
    throw err
  })
  return _authReady
}

export interface StorageUploadResult {
  fileId: string
  fileName: string
  mimeType: string
  sizeBytes: number
}

/** 是否处于登录态（token 存在） */
export { hasSession }

/**
 * Web 端统一上传入口（add-auth-login）：
 * 1. 携带业务 token 调 `storage-upload-ticket` 签发限路径限时凭证
 * 2. 客户端携带 Authorization 直传 COS（无 TCB 匿名会话依赖）
 */
export async function uploadStorageFile(
  file: File,
  cloudPath: string,
  onProgress?: (percent: number) => void,
): Promise<StorageUploadResult> {
  // cloudPath 形如 `lead/<leadId>/xxx.png`；dir = 目录段，filename = 末段
  const slashIdx = cloudPath.lastIndexOf('/')
  const dir = slashIdx > 0 ? cloudPath.slice(0, slashIdx) : 'misc'
  const filename = slashIdx >= 0 ? cloudPath.slice(slashIdx + 1) : cloudPath

  const ticket = await callFunction<{
    uploadUrl: string
    token: string
    authorization: string
    fileId: string
    cosFileId: string
    expiresIn: number
  }>('storage-upload-ticket', {
    data: { dir, filename },
  })

  // 凭证完整性防御：storage-upload-ticket 须返回限路径直传凭证的完整四要素，
  // 任一为空即说明该 storage 云函数为旧版 bundle（未随 fix-web-upload-forbidden 部署）——
  // 缺 token / cosFileId 时浏览器发出的空请求头会得到难定位的 COS 403，
  // 这里提前给出可执行提示而非让 COS 返回 403。
  const credMissing: string[] = []
  if (!ticket.uploadUrl) credMissing.push('uploadUrl')
  if (!ticket.authorization) credMissing.push('authorization')
  if (!ticket.token) credMissing.push('token')
  if (!ticket.cosFileId) credMissing.push('cosFileId')
  if (credMissing.length > 0) {
    throw new Error(
      `存储凭证不完整：${credMissing.join('/')} 为空。` +
        'storage-upload-ticket 云函数可能仍为未透传 token/cosFileId 的旧版 bundle，' +
        '请先重新部署 system 域（含 storage-upload-ticket）后再上传。',
    )
  }

  await putFileDirect(
    ticket.uploadUrl,
    ticket.authorization,
    ticket.token,
    ticket.cosFileId,
    file,
    onProgress,
  )

  const fileId = ticket.fileId
  if (typeof fileId !== 'string' || fileId.length === 0) {
    throw new Error('存储凭证签发成功但未返回 fileId')
  }
  return { fileId, fileName: file.name, mimeType: file.type, sizeBytes: file.size }
}

/** 直传 COS：PUT uploadUrl + Authorization 头。 */
async function putFileDirect(
  uploadUrl: string,
  authorization: string,
  token: string,
  cosFileId: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl, true)
    xhr.setRequestHeader('Authorization', authorization)
    xhr.setRequestHeader('X-Cos-Security-Token', token)
    xhr.setRequestHeader('X-Cos-Meta-Fileid', cosFileId)
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        onProgress?.(Math.round((e.loaded * 100) / e.total))
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        // COS 失败时透传响应体中的错误码，便于区分签名不匹配 / 权限不足 / 时间偏差
        const detail = describeCosError(xhr.responseText)
        reject(new Error(`上传失败：HTTP ${xhr.status}${detail}`))
      }
    }
    xhr.onerror = () => reject(new Error('上传失败：网络错误'))
    xhr.send(file)
  })
}

/** 从 COS 错误 XML 响应体提取错误码与描述，供 403 等失败快速定位根因。 */
function describeCosError(responseText: string): string {
  if (!responseText) return ''
  const code = /<Code[^>]*>([^<]+)<\/Code>/.exec(responseText)?.[1]
  const message = /<Message[^>]*>([^<]+)<\/Message>/.exec(responseText)?.[1]
  if (!code && !message) {
    const trimmed = responseText.slice(0, 200).trim()
    return trimmed ? `：${trimmed}` : ''
  }
  return `：COS ${code ?? '未知错误'}${message ? `（${message}）` : ''}`
}

/**
 * 根据 fileId 获取临时访问地址（云函数签发，1 小时有效），不持久化 URL。
 * 服务端为部分成功语义：跨租户/缺失/非 cloud:// 的 fileId 跳过（不进结果），
 * 其余正常返回；返回 map 仅含可解析的 fileId，调用方以 `urls[fileId]` 判空即可。
 */
export async function getStorageTempUrls(fileIds: string[]): Promise<Record<string, string>> {
  if (fileIds.length === 0) return {}
  return callFunction<Record<string, string>>('storage-get-temp-urls', {
    data: { fileIds },
  })
}

/**
 * 删除 Storage 对象（add-auth-login）。
 * 文件删除已迁移到业务云函数服务端执行（如 lead-attachment-delete 联动删除），
 * 客户端不再直接调用 TCB Storage 删除 API（匿名会话已移除）。
 * 此函数保留以兼容调用方；业务元数据由对应云函数删除。
 */
export async function deleteStorageFiles(fileIds: string[]): Promise<void> {
  if (fileIds.length === 0) return
  // 服务端业务函数删除元数据时会联动删除文件本体；
  // 若调用方需要立即删除孤立文件，可扩展 storage-delete-files 云函数。
  void fileIds
}

/**
 * 通用 callFunction 包装（add-auth-login）：
 * 自动注入 data.token（不再注入 tenantId），unwrap ResultEnvelope，失败抛 Error。
 *
 * 错误规范化：
 * - UNAUTHENTICATED → 清除本地登录态并跳转登录页（带回跳目标；按当前路由 /admin 分流）
 * - FORBIDDEN → 抛 Error（UI 统一"无操作权限"提示，不跳登录）
 * - 可选辅助同步可设置 redirectOnAuthError=false；仍抛错误，但不修改路由或会话
 * - 其他错误 → 抛 Error(message + code)
 *
 * 平台超管调用（api/platform.ts，add-platform-admin-web）：
 * 通过第三个参数 `options.token` 显式注入平台 token（`null` = 不注入 token，
 * 用于公开函数 platform-login）；未传时保持默认行为（注入员工会话 token）。
 */
export async function callFunction<T>(
  name: string,
  payload: { data?: Record<string, unknown> } = {},
  options?: { token?: string | null; redirectOnAuthError?: boolean },
): Promise<T> {
  // 请求期间可能切换工作区；旧员工响应不得清除平台身份，反之亦然。
  const requestScope = getPageSessionScope()
  const token = options?.token !== undefined ? options.token : getSessionToken()
  const domain = resolveFunctionDomain(name)
  const employeeBusiness = domain !== 'platform' && !PUBLIC_WEB_FUNCTIONS.has(name) && name !== 'auth-logout'
  const assertEmployeeRequestCurrent = (): void => {
    if (employeeBusiness && (isEmployeeSessionRetiring(token)
      || (token === null && retiringEmployeeTokens.size > 0)
      || token !== getSessionToken() || requestScope !== getPageSessionScope())) {
      // 尚未发往云端的旧请求明确取消，不能伪造业务成功或把新身份注入旧调用。
      throw cancelledSessionRequest()
    }
  }
  assertEmployeeRequestCurrent()
  // 视觉回归/测试 mock 缝：双 gate 保证生产构建静态判死（DEV=false && MODE!=='test'），
  // fixtures 走 dynamic import，只 mock 模式加载、不进生产包。
  if (
    (import.meta.env.DEV || import.meta.env.MODE === 'test') &&
    import.meta.env.VITE_TCB_MOCK === '1'
  ) {
    const { mockCallFunction } = await import('../fixtures')
    assertEmployeeRequestCurrent()
    return mockCallFunction(name, payload.data) as Promise<T>
  }
  const app = await getApp()
  await ensureTransportAuth()
  assertEmployeeRequestCurrent()
  const request = (async (): Promise<T> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let res: any
    try {
      res = await app.callFunction({
        name: domain,
        data: {
          ...(payload.data ?? {}),
          functionName: name,
          ...(token ? { token } : {}),
        },
      })
    } catch (raw) {
      const err = normalizeCallError(raw, name)
      // 网络/传输层错误同样需在控制台与 CLS 可见
      // eslint-disable-next-line no-console
      console.error('[tcb] callFunction network error', { name, domain, data: payload.data, err: err.message, stack: err.stack })
      try {
        createFrontendLogger({ source: 'tcb', fn: name }).error('callFunction network error', { name, domain, code: (err as unknown as { code?: string })?.code, message: err.message })
      } catch {
        // 日志模块加载失败不影响主错误抛出
      }
      throw err
    }
    return unwrap<T>(res, name, {
      redirectOnAuthError: options?.redirectOnAuthError !== false && requestScope === getPageSessionScope()
        && requestScope === (domain === 'platform' ? 'platform' : 'employee')
        && token === (domain === 'platform' ? getPlatformToken() : getSessionToken()),
    })
  })()
  if (!employeeBusiness || !token) return request
  let requests = employeeRequests.get(token)
  if (!requests) {
    requests = new Set()
    employeeRequests.set(token, requests)
  }
  requests.add(request)
  try {
    return await request
  } finally {
    requests.delete(request)
    if (requests.size === 0) employeeRequests.delete(token)
  }
}

configureFrontendLogTransport((entries) =>
  callFunction('log-report', { data: { entries } }),
)

/** 把 TCB 响应（res.result 是 ResultEnvelope）解包，失败抛 Error。 */
export function unwrap<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  res: any,
  fnName: string,
  options?: { redirectOnAuthError?: boolean },
): T {
  const envelope = res?.result as ResultEnvelope<T> | undefined
  if (!envelope) {
    const raw = safeStringify(res)
    // eslint-disable-next-line no-console
    console.error('[tcb] envelope missing', { fnName, res, raw })
    throw new Error(
      `[tcb] 函数 ${fnName} 响应异常：缺少 result 字段。原始响应: ${raw}`,
    )
  }
  if (!envelope.ok) {
    const code = envelope.error?.code ?? 'UNKNOWN'
    const message = envelope.error?.message ?? '云函数返回失败'
    const details = envelope.error?.details
    // 业务错误统一打印：尤其 INTERNAL，需携带 code/details 帮助定位已有客户等问题
    // eslint-disable-next-line no-console
    console.error('[tcb] business error', { fnName, code, message, details, envelope })
    const err = new Error(`${code}: ${message}`)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(err as any).code = code
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(err as any).details = details
    const credentialCheckFunctions = new Set([
      'auth-login',
      'auth-web-password-login',
      'auth-web-password-select',
      'auth-wx-login',
      'auth-change-password',
      'platform-login',
      'platform-change-password',
      'platform-reauth',
    ])
    const expiredChangePasswordSession = fnName === 'auth-change-password' &&
      typeof details === 'object' && details !== null && 'field' in details && details.field === 'token'
    if (options?.redirectOnAuthError !== false && code === 'UNAUTHENTICATED' && (!credentialCheckFunctions.has(fnName) || expiredChangePasswordSession)) {
      handleUnauthenticated()
    }
    if (
      options?.redirectOnAuthError !== false &&
      getPageSessionScope() === 'employee' &&
      code === 'FORBIDDEN' &&
      details &&
      typeof details === 'object' &&
      'field' in details &&
      details.field === 'subscription' &&
      !window.location.hash.includes('/subscription-expired')
    ) {
      window.location.hash = '/subscription-expired'
    }
    // 非预期错误（INTERNAL）额外上报到 CLS，便于无控制台时回溯
    if (code === 'INTERNAL' || code === 'UNKNOWN') {
      try {
        createFrontendLogger({ source: 'tcb', fn: fnName }).error('business error', { code, message, details })
      } catch {
        // 上报失败静默
      }
    }
    throw err
  }
  return envelope.data
}

/**
 * UNAUTHENTICATED：清除对应登录态并跳登录页（带回跳目标）。
 * 按当前路由分流（add-platform-admin-web）：`/admin/*` 下清平台 token 跳 `/admin/login`，
 * 业务路由清员工 token 跳 `/login`（两套会话互不识别）。
 */
export function handleUnauthenticated(): void {
  const current = window.location.hash.replace(/^#/, '')
  const isAdmin = getPageSessionScope() === 'platform'
  const loginPath = isAdmin ? '/admin/login' : '/login'
  const onLoginPage =
    current === loginPath ||
    current.startsWith(`${loginPath}?`) ||
    current.startsWith(`${loginPath}#`)
  const redirect =
    current && !onLoginPage && !current.startsWith('/login') && !current.startsWith('/admin/login')
      ? `?redirect=${encodeURIComponent(current)}`
      : ''

  if (isAdmin) clearPlatformToken()
  else clearSession()

  if (!onLoginPage) {
    ElMessage.warning('登录已失效，请重新登录')
    window.location.hash = `${loginPath}${redirect}`
  }
}

/** 把任意 thrown value 规范化成 Error，避免 [object Object]。 */
export function normalizeCallError(raw: unknown, fnName: string): Error {
  if (raw instanceof Error) return raw
  // axios 错误对象：message / response.data / config.url
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ax = raw as any
  if (ax && typeof ax === 'object') {
    const status = ax?.response?.status ?? ax?.statusCode
    const data = ax?.response?.data ?? ax?.data
    const url = ax?.config?.url ?? ax?.url
    const detail = typeof data === 'string' ? data : safeStringify(data)
    const msg =
      ax.message ??
      (status ? `HTTP ${status}` : null) ??
      `调用 ${fnName} 失败`
    const err = new Error(
      `[tcb] ${msg}${url ? ` (${url})` : ''}${detail ? `\n${detail}` : ''}`,
    )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(err as any).code = ax?.code ?? (status ? `HTTP_${status}` : 'NETWORK')
    return err
  }
  return new Error(`[tcb] 调用 ${fnName} 失败: ${String(raw)}`)
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v)
  } catch {
    return String(v)
  }
}
